/**
 * Trigger Select Component
 *
 * Dropdown for selecting which trigger type to use.
 * Used by AutomationCard to let users pick from action's available triggers.
 *
 * Note: When reminderType is a sellable type (workout_plan, group, etc.),
 * we display 'some' as selected since that's the "purchased specific" trigger.
 */

import { useFormikContext } from 'formik';
import { FC, useLayoutEffect, useState } from 'react';
import { Box } from 'src/components/Box';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { TRIGGERS, getTriggerOptionsFromIds } from '../automationTriggers';
import { AutomationItem } from '../types';

// Sellable types that map to 'some' trigger
const SELLABLE_TYPES = ['workout_plan', 'group', 'product', 'package'];

interface TriggerSelectProps {
  index: number;
  availableTriggers: string[]; // Trigger IDs available for this action
  onChange?: (triggerId: string) => void;
}

export const TriggerSelect: FC<TriggerSelectProps> = ({
  index,
  availableTriggers,
  onChange,
}) => {
  const { setFieldValue, values } = useFormikContext<{
    automations: AutomationItem[];
  }>();

  const triggerOptions = getTriggerOptionsFromIds(availableTriggers);
  const automation = values.automations[index];
  const reminderType = automation?.reminderType || '';

  // Shadow field for display - maps sellable types back to 'some'
  const fieldName = `automations.${index}._triggerSelectValue`;
  const [initialized, setInitialized] = useState(false);

  // Initialize shadow field - sellable types should display as 'some'
  useLayoutEffect(() => {
    const displayValue =
      SELLABLE_TYPES.includes(reminderType) && availableTriggers.includes('some')
        ? 'some'
        : reminderType;
    setFieldValue(fieldName, displayValue);
    setInitialized(true);
  }, [reminderType, fieldName, setFieldValue, availableTriggers]);

  const handleChange = (newTrigger: string) => {
    setFieldValue(`automations.${index}.reminderType`, newTrigger);

    // Reset related fields when trigger changes
    setFieldValue(`automations.${index}.reminderTypeId`, null);
    setFieldValue(`automations.${index}.stripePlan`, null);
    setFieldValue(`automations.${index}.afterTrial`, 0);
    setFieldValue(`automations.${index}.everyPayment`, 0);

    // Set default delay type based on trigger
    const newTriggerConfig = TRIGGERS[newTrigger];
    if (newTriggerConfig?.delayTypes?.length) {
      setFieldValue(
        `automations.${index}.delayType`,
        newTriggerConfig.delayTypes[0]
      );
    }

    onChange?.(newTrigger);
  };

  if (!initialized) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 300 }}>
      <FormSelect
        name={fieldName}
        options={triggerOptions}
        prompt="Select trigger..."
        onChange={(e) => handleChange(e.target.value as string)}
      />
    </Box>
  );
};
